/**
 * Custom hook for managing chat settings and configuration
 *
 * Handles AI settings loading, model/temperature changes, and suggested content toggles.
 * Extracted from ChatPanel.tsx to reduce complexity.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../../../services/api';
import { eventBus, EventNames, useEventListener } from '../../../utils/eventBus';

export interface UseChatSettingsProps {
  onError?: (message: string) => void;
}

export interface UseChatSettingsReturn {
  // State
  suggestedQuestionsEnabled: boolean;
  suggestedTasksEnabled: boolean;
  configRefreshTrigger: number;
  documentSelectionMode: 'manual' | 'smart';

  // Handlers
  handleModelChange: (modelId: string) => Promise<void>;
  handleTemperatureChange: (temperature: number) => void;
  handleSuggestedQuestionsToggle: (enabled: boolean) => Promise<void>;
  handleSuggestedTasksToggle: (enabled: boolean) => Promise<void>;
}

/**
 * Hook to manage chat settings and configuration
 */
export function useChatSettings({ onError }: UseChatSettingsProps = {}): UseChatSettingsReturn {
  const [suggestedQuestionsEnabled, setSuggestedQuestionsEnabled] = useState(true);
  const [suggestedTasksEnabled, setSuggestedTasksEnabled] = useState(true);
  const [configRefreshTrigger, setConfigRefreshTrigger] = useState(0);
  const [documentSelectionMode, setDocumentSelectionMode] = useState<'manual' | 'smart'>('smart');
  const temperatureTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load AI Settings on mount
  useEffect(() => {
    const loadAISettings = async () => {
      try {
        const response = await apiService.getAISettings();
        if (response.success && response.settings?.settings) {
          const settings = response.settings.settings;
          setSuggestedQuestionsEnabled(settings.suggested_questions_enabled ?? true);
          setSuggestedTasksEnabled(settings.suggested_tasks_enabled ?? true);
          const rawMode = settings.document_selection_mode;
          // Backward compat: map legacy values to 'smart'
          const mode = rawMode === 'manual' ? 'manual' : 'smart';
          setDocumentSelectionMode(mode);
          eventBus.emit('doc_selection_mode_changed', { mode });
        }
      } catch (err) {
        console.error('Error loading AI settings:', err);
      }
    };
    loadAISettings();
  }, []);

  // Cleanup temperature timeout
  useEffect(() => {
    return () => {
      if (temperatureTimeoutRef.current) {
        clearTimeout(temperatureTimeoutRef.current);
      }
    };
  }, []);

  const handleModelChange = useCallback(async (modelId: string) => {
    try {
      const response = await apiService.updateConfigQuick({ model: modelId });
      if (response.success) {
        setTimeout(() => setConfigRefreshTrigger((prev) => prev + 1), 300);
      }
    } catch (err) {
      console.error('Error updating model:', err);
    }
  }, []);

  const handleTemperatureChange = useCallback((temperature: number) => {
    if (temperatureTimeoutRef.current) {
      clearTimeout(temperatureTimeoutRef.current);
    }
    temperatureTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await apiService.updateConfigQuick({ temperature });
        if (response.success) {
          setConfigRefreshTrigger((prev) => prev + 1);
        }
      } catch (err) {
        console.error('Error updating temperature:', err);
      }
    }, 500);
  }, []);

  const handleSuggestedQuestionsToggle = useCallback(
    async (enabled: boolean) => {
      try {
        setSuggestedQuestionsEnabled(enabled);
        eventBus.emit(EventNames.SUGGESTED_QUESTIONS_TOGGLED, { enabled });
        await apiService.updateAISettings({ suggested_questions_enabled: enabled });
      } catch (err) {
        console.error('Error updating suggested questions setting:', err);
        setSuggestedQuestionsEnabled(!enabled);
        onError?.('Error al actualizar configuracion');
      }
    },
    [onError]
  );

  const handleSuggestedTasksToggle = useCallback(
    async (enabled: boolean) => {
      try {
        setSuggestedTasksEnabled(enabled);
        eventBus.emit(EventNames.SUGGESTED_TASKS_TOGGLED, { enabled });
        await apiService.updateAISettings({ suggested_tasks_enabled: enabled });
      } catch (err) {
        console.error('Error updating suggested tasks setting:', err);
        setSuggestedTasksEnabled(!enabled);
        onError?.('Error al actualizar configuracion');
      }
    },
    [onError]
  );

  // doc_selection_mode_changed listener is in ChatInputArea (which also clears suggestions).
  // useChatSettings already sets its own state at line 52 on mount — no self-listener needed.
  useEventListener(EventNames.SUGGESTED_QUESTIONS_TOGGLED, (data: { enabled: boolean }) => {
    setSuggestedQuestionsEnabled(data.enabled);
  });
  useEventListener(EventNames.SUGGESTED_TASKS_TOGGLED, (data: { enabled: boolean }) => {
    setSuggestedTasksEnabled(data.enabled);
  });

  return {
    suggestedQuestionsEnabled,
    suggestedTasksEnabled,
    configRefreshTrigger,
    documentSelectionMode,
    handleModelChange,
    handleTemperatureChange,
    handleSuggestedQuestionsToggle,
    handleSuggestedTasksToggle,
  };
}

export default useChatSettings;
